import { useEffect, useRef, useState } from "react";
import { Search } from "lucide-react";
import { citiesFor, OTHER_CITY } from "@/constants/cities";

const STATES = [
  "Andhra Pradesh","Arunachal Pradesh","Assam","Bihar","Chhattisgarh","Goa","Gujarat","Haryana",
  "Himachal Pradesh","Jharkhand","Karnataka","Kerala","Madhya Pradesh","Maharashtra","Manipur",
  "Meghalaya","Mizoram","Nagaland","Odisha","Punjab","Rajasthan","Sikkim","Tamil Nadu",
  "Telangana","Tripura","Uttar Pradesh","Uttarakhand","West Bengal","Andaman & Nicobar",
  "Chandigarh","Dadra & Nagar Haveli and Daman & Diu","Delhi","Jammu & Kashmir","Ladakh",
  "Lakshadweep","Puducherry",
];

/**
 * Combined State + City picker with a searchable city dropdown.
 *
 * Behaviour:
 *  - State  → native <select> (36 states/UTs).
 *  - City   → searchable dropdown populated dynamically from the selected
 *             state. If the user picks "Other", a free-text input appears.
 *  - Changing the state clears the city so the two never drift out of sync.
 *
 * Props:
 *  - state, city, cityOther: current values
 *  - onChange({state, city, cityOther}): merged setter
 *  - required, disabled, compact
 *  - testidPrefix: e.g. "reg" produces "reg-state" / "reg-city"
 *  - layout: "grid" (two cols side-by-side) | "stacked"
 */
export default function StateCitySelect({
  state = "",
  city = "",
  cityOther = "",
  onChange,
  required = false,
  disabled = false,
  compact = false,
  testidPrefix = "loc",
  layout = "grid",
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const boxRef = useRef(null);

  useEffect(() => {
    const h = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const cities = citiesFor(state);
  const filtered = q ? cities.filter((c) => c.toLowerCase().includes(q.toLowerCase())) : cities;
  const isOther = city === OTHER_CITY;

  const setState = (v) => {
    // Reset city when state changes so the two stay consistent
    onChange({ state: v, city: "", cityOther: "" });
  };
  const setCity = (v) => onChange({ state, city: v, cityOther: v === OTHER_CITY ? cityOther : "" });
  const setCityOther = (v) => onChange({ state, city, cityOther: v });

  const labelCls = compact
    ? "text-[10px] uppercase tracking-[0.3em] text-navy/60"
    : "text-[11px] uppercase tracking-widest text-navy/60";
  const inputCls = "mt-1 w-full border border-border px-3 py-3 text-sm rounded-sm focus:border-gold focus:outline-none focus:ring-2 focus:ring-gold/20 transition bg-white";

  const wrap = layout === "grid"
    ? "grid grid-cols-1 md:grid-cols-2 gap-3"
    : "space-y-3";

  return (
    <div className={wrap}>
      {/* State */}
      <div>
        <label className={labelCls}>State {required && <span className="text-rose">*</span>}</label>
        <select
          data-testid={`${testidPrefix}-state`}
          required={required}
          disabled={disabled}
          value={state}
          onChange={(e) => setState(e.target.value)}
          className={inputCls}
        >
          <option value="">Select state…</option>
          {STATES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      {/* City — searchable dropdown */}
      <div ref={boxRef} className="relative">
        <label className={labelCls}>City {required && <span className="text-rose">*</span>}</label>
        <button
          type="button"
          data-testid={`${testidPrefix}-city-toggle`}
          disabled={disabled || !state}
          onClick={() => setOpen((o) => !o)}
          className={`${inputCls} text-left flex items-center justify-between ${!city ? "text-navy/40" : "text-navy"} ${!state ? "opacity-60 cursor-not-allowed" : ""}`}
        >
          <span className="truncate">
            {!state ? "Select a state first…" : isOther ? (cityOther || "Other (type manually)") : (city || "Select your city…")}
          </span>
          <span className="text-navy/40 text-xs ml-2 shrink-0">▾</span>
        </button>
        {open && state && (
          <div className="absolute z-30 mt-1 w-full max-h-72 overflow-hidden bg-white border border-line rounded-sm shadow-lg">
            <div className="p-2 border-b border-line/70 bg-ivory/60 flex items-center gap-2">
              <Search size={12} className="text-navy/40" />
              <input
                autoFocus
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search city…"
                data-testid={`${testidPrefix}-city-search`}
                className="w-full bg-transparent text-sm outline-none"
              />
            </div>
            <div className="max-h-56 overflow-y-auto py-1">
              {filtered.length === 0 && (
                <div className="px-3 py-6 text-xs text-navy/50 text-center">No cities match "{q}"</div>
              )}
              {filtered.map((c) => (
                <button
                  type="button"
                  key={c}
                  data-testid={`${testidPrefix}-city-opt-${c.replace(/\s+/g, "-").toLowerCase()}`}
                  onClick={() => { setCity(c); setOpen(false); setQ(""); }}
                  className={`w-full text-left px-3 py-1.5 text-sm hover:bg-cream/60 ${c === city ? "bg-gold/[0.08] text-royal font-medium" : "text-navy"}`}
                >
                  {c}
                </button>
              ))}
              <div className="border-t border-line/60 mt-1">
                <button
                  type="button"
                  data-testid={`${testidPrefix}-city-other`}
                  onClick={() => { setCity(OTHER_CITY); setOpen(false); setQ(""); }}
                  className={`w-full text-left px-3 py-1.5 text-sm hover:bg-cream/60 ${isOther ? "bg-gold/[0.08] text-royal font-medium" : "text-gold-deep"}`}
                >
                  {OTHER_CITY}
                </button>
              </div>
            </div>
          </div>
        )}
        {isOther && (
          <input
            data-testid={`${testidPrefix}-city-manual`}
            value={cityOther || ""}
            onChange={(e) => setCityOther(e.target.value)}
            placeholder="Enter your city / town / village"
            disabled={disabled}
            className={`${inputCls} mt-2`}
          />
        )}
      </div>
    </div>
  );
}
